/**
 * OverlayHost — ONE bottom-right corner for everything that interrupts a
 * recruiter (streamline plan D-2). Mounted lazily by CRMApp behind the
 * `overlays` beta flag; the six independent hosts remain the toggle-off path.
 *
 * What it replaces: five hosts that each owned a corner, a z-index, a poll, a
 * ticker, a permission prompt and a chime, and none of which knew the others
 * existed. That is how a hot lead could be sliced off at three, an inbound text
 * could be dropped so a newer one could show, an SMS toast could sit in the
 * bottom-LEFT while everything else stacked bottom-right, and how a page could
 * carry 10.5rem of dead bottom padding reserved for overlays that were usually
 * not there.
 *
 * The shape here is two fixed containers in the same corner:
 *
 *   ┌─ notification stack (z-40) ─┐   top 3 cards + a "+N more" pill; the
 *   │  card / card / card / +N    │   overflow QUEUES and is never dropped
 *   ├─ call dock (z-50) ──────────┤   SoftphoneBar, measured with a
 *   │  softphone                  │   ResizeObserver so the stack sits on top
 *   └─────────────────────────────┘   of it — and closes the gap when idle
 *
 * The dock stays the top tier because an incoming call's Answer button must
 * beat every toast and every modal; that is the one ordering rule this whole
 * corner exists to protect.
 *
 * One 1s ticker drives countdowns, the escalate-at-T-0 sweep, the callback
 * fire-window evaluation and the auto-dismiss budgets. One lazy permission
 * request. One chime. Feed semantics (endpoints, dedupe keys, poll cadences,
 * visibility gate, claim/escalate flows) are the v1 ones — see the three
 * use*Feed hooks, which document every difference.
 *
 * QA: `window.dispatchEvent(new CustomEvent('crm:overlay-test-fire', { detail:
 * { kind: 'lead_call' } }))` injects a synthetic card. Test cards never call an
 * API and never navigate — they are safe to fire in production. Outreach
 * lists add two kinds: `outreach_assigned` (the "you've been given a list"
 * card) and `sms_list` (a text toast carrying "Reply on <list name>").
 *
 * The outreach-assigned feed has no poll of its own: it reads the nav counts'
 * existing worklist poll (docs/crm-outreach-lists-plan.md §B7).
 *
 * Neither has the mention feed (a teammate named you in a note, or replied to
 * one of yours): it reads the same counts' `mentions` bucket and asks for the
 * words only when a new mention appears
 * (docs/plans/crm-report-builder-search-collab-plan.md B3.4). QA fires its card
 * with `{ kind: 'mention' }`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useSoftphone } from '../../contexts/SoftphoneContext';
import type { LastCall } from '../../contexts/SoftphoneContext';
import SoftphoneBar from '../SoftphoneBar';
import { KINDS } from './alertKinds';
import type { AlertKind, CrmAlert } from './alertKinds';
import { Z } from '../ui/z';
import { createItem, orderItems, replaceSource, resetBudget, selectVisible, tick, upsert } from './overlayQueue';
import type { OverlayItem, OverlayKind } from './overlayQueue';
import { useAlertsFeed } from './useAlertsFeed';
import { useCallbacksFeed } from './useCallbacksFeed';
import type { CallbackCard } from './useCallbacksFeed';
import { useSmsFeed } from './useSmsFeed';
import type { SmsCard } from './useSmsFeed';
import { useReviewsFeed } from './useReviewsFeed';
import type { ReviewReadyCard } from './useReviewsFeed';
import { useOutreachFeed } from './useOutreachFeed';
import type { OutreachAssignedCard } from './useOutreachFeed';
import { useMentionsFeed } from './useMentionsFeed';
import type { MentionCard } from './useMentionsFeed';
import {
  AlertCardBody, CallbackCardBody, MentionCardBody, OutreachAssignedCardBody, OverflowPill, RatingCardBody,
  ReviewReadyCardBody, SmsCardBody,
} from './OverlayCards';

const TICK_MS = 1_000;
/** Breathing room between the call dock and the card sitting on top of it. */
const DOCK_GAP_PX = 8;
const TEST_EVENT = 'crm:overlay-test-fire';

/**
 * Both fixed containers share this anchor: tight to the corner from lg up,
 * lifted below lg to clear whichever bottom chrome that page docks — the h-14
 * mobile tab bar on list pages, and on the lead page (which has no tab bar)
 * the D-12 rail-sheet peek, whose collapsed height is ~110px on phones (44px
 * handle + 8px gap + the ~58px status row). 7.5rem covers both; before D-12
 * this was 4.5rem and the peek's handle button was eating the bottom of the
 * hot-lead card's Open/Ignore row (seam-5 L2).
 */
const CORNER = 'fixed bottom-[calc(7.5rem+env(safe-area-inset-bottom))] right-4 lg:bottom-4';

type Payload =
  | { t: 'alert'; alert: CrmAlert }
  | { t: 'callback'; card: CallbackCard }
  | { t: 'sms'; card: SmsCard }
  | { t: 'rating'; call: LastCall }
  | { t: 'review'; card: ReviewReadyCard }
  | { t: 'outreach'; card: OutreachAssignedCard }
  | { t: 'mention'; card: MentionCard };

type Item = OverlayItem<Payload>;

/** QA-injected cards. They never touch the network and never navigate. */
const isTestId = (id: string) => id.startsWith('test-');

const toMs = (iso: string | null | undefined, fallback: number): number => {
  const ms = iso ? new Date(iso).getTime() : NaN;
  return Number.isFinite(ms) ? ms : fallback;
};

export default function OverlayHost() {
  const { user } = useAuth();
  const { lastCall, clearLastCall } = useSoftphone();

  const [nowMs, setNowMs] = useState(() => Date.now());
  const [items, setItems] = useState<Item[]>([]);
  const [testItems, setTestItems] = useState<Item[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [dockH, setDockH] = useState(0);

  const userRef = useRef(user);
  userRef.current = user;

  // Destructured, not held as feed objects: the hooks' callbacks are stable and
  // the effects below depend on them individually.
  const { alerts, dismiss: dismissAlert, open: openAlert } = useAlertsFeed(nowMs);
  const { card: callbackCard, dismiss: dismissCallback, open: openCallback } = useCallbacksFeed(nowMs);
  const { cards: smsCards, dismiss: dismissSms, open: openSms } = useSmsFeed();
  const { cards: reviewCards, dismiss: dismissReview, open: openReview } = useReviewsFeed();
  // No poll: rides the nav counts' existing worklist poll (see the hook).
  const { cards: outreachCards, dismiss: dismissOutreach, open: openOutreach } = useOutreachFeed();
  // No poll either: reads the same counts' `mentions` bucket (see the hook).
  const { cards: mentionCards, dismiss: dismissMention, open: openMention } = useMentionsFeed();

  /* ------------------------ one permission request ----------------------- */

  const permAskedRef = useRef(false);
  useEffect(() => {
    if (permAskedRef.current) return;
    permAskedRef.current = true;
    try {
      if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
        Notification.requestPermission().catch(() => {});
      }
    } catch {
      /* unsupported — the in-app cards still fire */
    }
  }, []);

  /* --------------------------- feeds → the queue -------------------------- */

  useEffect(() => {
    setItems((prev) => replaceSource(prev, 'alerts', alerts.map((a) => createItem<Payload>({
      id: a.id,
      kind: a.kind as OverlayKind,
      createdAt: toMs(a.created_at, Date.now()),
      payload: { t: 'alert', alert: a },
    }))));
  }, [alerts]);

  useEffect(() => {
    setItems((prev) => replaceSource(prev, 'callback', callbackCard ? [createItem<Payload>({
      id: callbackCard.id,
      kind: 'callback',
      payload: { t: 'callback', card: callbackCard },
    })] : []));
  }, [callbackCard]);

  useEffect(() => {
    setItems((prev) => replaceSource(prev, 'sms', smsCards.map((c) => createItem<Payload>({
      id: c.id,
      kind: 'sms',
      createdAt: toMs(c.occurredAt, Date.now()),
      payload: { t: 'sms', card: c },
    }))));
  }, [smsCards]);

  useEffect(() => {
    setItems((prev) => replaceSource(prev, 'reviews', reviewCards.map((c) => createItem<Payload>({
      id: c.id,
      kind: 'review_ready',
      createdAt: toMs(c.completedAt, Date.now()),
      payload: { t: 'review', card: c },
    }))));
  }, [reviewCards]);

  useEffect(() => {
    setItems((prev) => replaceSource(prev, 'outreach', outreachCards.map((c) => createItem<Payload>({
      id: c.id,
      kind: 'outreach_assigned',
      createdAt: toMs(c.launchedAt, Date.now()),
      payload: { t: 'outreach', card: c },
    }))));
  }, [outreachCards]);

  useEffect(() => {
    setItems((prev) => replaceSource(prev, 'mentions', mentionCards.map((c) => createItem<Payload>({
      id: c.id,
      kind: 'mention',
      createdAt: toMs(c.createdAt, Date.now()),
      payload: { t: 'mention', card: c },
    }))));
  }, [mentionCards]);

  useEffect(() => {
    setItems((prev) => replaceSource(prev, 'rating', lastCall ? [createItem<Payload>({
      id: `rating:${lastCall.callSid || lastCall.contactId}`,
      kind: 'rating',
      payload: { t: 'rating', call: lastCall },
    })] : []));
  }, [lastCall]);

  useEffect(() => {
    setItems((prev) => replaceSource(prev, 'test', testItems));
  }, [testItems]);

  /* ------------------------------ dismissal ------------------------------ */

  // Dismissal always goes through the OWNING feed: removing a card from the
  // queue alone would let the feed's next refresh put it straight back, with a
  // fresh clock.
  const dismissItem = useCallback((item: Item) => {
    if (isTestId(item.id)) {
      setTestItems((prev) => prev.filter((i) => i.id !== item.id));
      return;
    }
    switch (item.payload.t) {
      case 'alert': dismissAlert(item.id); break;
      case 'callback': dismissCallback(item.id); break;
      case 'sms': dismissSms(item.id); break;
      case 'review': dismissReview(item.id); break;
      case 'outreach': dismissOutreach(item.id); break;
      case 'mention': dismissMention(item.id); break;
      case 'rating': clearLastCall(); break;
      default: break;
    }
  }, [dismissAlert, dismissCallback, dismissSms, dismissReview, dismissOutreach, dismissMention, clearLastCall]);

  /* ------------------------------ one ticker ------------------------------ */

  const lastTickRef = useRef(Date.now());
  // Cards whose budget ran out inside the state updater; their feeds are told
  // after the commit (a state updater must stay pure).
  const expiredRef = useRef<Item[]>([]);

  useEffect(() => {
    const id = window.setInterval(() => {
      const t = Date.now();
      // Real elapsed, not the nominal interval: a throttled background tab must
      // not hand a card an extra ten minutes of budget it never spent visibly.
      const elapsed = Math.max(0, t - lastTickRef.current);
      lastTickRef.current = t;
      setNowMs(t);
      setItems((prev) => {
        const res = tick(prev, elapsed);
        if (res.expired.length) expiredRef.current = [...expiredRef.current, ...res.expired];
        return res.changed ? res.items : prev;
      });
    }, TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  // Drain after every commit. dismissItem is idempotent, so a StrictMode
  // double-invoked updater cannot double-dismiss anything.
  useEffect(() => {
    if (!expiredRef.current.length) return;
    const drained = expiredRef.current;
    expiredRef.current = [];
    for (const item of drained) dismissItem(item);
  });

  /* ---------------------- the dock, measured not guessed ------------------- */

  const dockRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = dockRef.current;
    if (!el) return undefined;
    // Height is 0 whenever SoftphoneBar renders null, which is most of the day —
    // the stack then sits flush in the corner instead of floating above a gap.
    const measure = () => setDockH(el.offsetHeight);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /* ------------------------------- test fire ------------------------------ */

  const pushTest = useCallback((item: Item) => setTestItems((prev) => upsert(prev, item)), []);

  useEffect(() => {
    const onFire = (e: Event) => {
      const detail = ((e as CustomEvent).detail || {}) as { kind?: string };
      const kind = String(detail.kind || 'lead_call');
      const now = Date.now();
      const id = `test-${now}`;
      if (kind === 'callback') {
        pushTest(createItem<Payload>({
          id, kind: 'callback', source: 'test', createdAt: now,
          payload: { t: 'callback', card: {
            id, callbackId: id, contactId: 'test', window: 'm10', firstName: 'Test Patient',
            scheduledAt: new Date(now + 10 * 60_000).toISOString(),
          } },
        }));
        return;
      }
      if (kind === 'sms' || kind === 'sms_list') {
        pushTest(createItem<Payload>({
          id, kind: 'sms', source: 'test', createdAt: now,
          payload: { t: 'sms', card: {
            id, contactId: 'test', name: 'Test Lead',
            preview: 'Is this about the study? I can talk this afternoon.',
            occurredAt: new Date(now).toISOString(),
            // `sms_list` previews the "Reply on <list name>" variant.
            listName: kind === 'sms_list' ? 'Test outreach list' : null,
          } },
        }));
        return;
      }
      if (kind === 'outreach_assigned') {
        pushTest(createItem<Payload>({
          id, kind: 'outreach_assigned', source: 'test', createdAt: now,
          payload: { t: 'outreach', card: {
            id, listId: 'test', listName: 'Test outreach list',
            line: 'Test Admin gave you 12 people for the Test Study',
            launchedAt: new Date(now).toISOString(),
          } },
        }));
        return;
      }
      if (kind === 'mention') {
        pushTest(createItem<Payload>({
          id, kind: 'mention', source: 'test', createdAt: now,
          payload: { t: 'mention', card: {
            id, mentionId: id, kind: 'mention', contactId: 'test', noteId: 'test', href: '/crm/contacts/test',
            line: 'Test Teammate mentioned you on Test Lead',
            snippet: 'Checked her records, @Test Recruiter can you call her back Tuesday?',
            createdAt: new Date(now).toISOString(),
          } },
        }));
        return;
      }
      if (kind === 'review_ready') {
        pushTest(createItem<Payload>({
          id, kind: 'review_ready', source: 'test', createdAt: now,
          payload: { t: 'review', card: {
            id, contactId: 'test', contactName: 'Test Lead', studyTitle: 'Test Study',
            status: 'completed', overallResult: 'review_required', pendingCount: 3,
            completedAt: new Date(now).toISOString(),
          } },
        }));
        return;
      }
      if (kind === 'rating') {
        pushTest(createItem<Payload>({
          id, kind: 'rating', source: 'test', createdAt: now,
          payload: { t: 'rating', call: { contactId: 'test', name: 'Test Lead', callSid: null, durationMs: 214_000 } },
        }));
        return;
      }
      // hasOwnProperty, not truthiness: 'toString' etc. are inherited keys and
      // must not mint a broken card (seam-1 verifier n4).
      if (!Object.prototype.hasOwnProperty.call(KINDS, kind)) return;
      const alert: CrmAlert = {
        id,
        kind: kind as AlertKind,
        contact_id: 'test',
        first_name: 'Test Lead',
        study_name: 'Test Study',
        status: 'assigned',
        assigned_user_id: userRef.current?.id ?? null,
        assigned_user_name: 'a recruiter',
        created_at: new Date(now - 45_000).toISOString(),
        escalate_at: new Date(now + 3 * 60_000).toISOString(),
        eligibility: 'eligible',
        org_name: 'Test Clinic',
        provider_name: 'Dr. Test',
      };
      pushTest(createItem<Payload>({
        id, kind: kind as OverlayKind, source: 'test', createdAt: now, payload: { t: 'alert', alert },
      }));
    };
    window.addEventListener(TEST_EVENT, onFire);
    return () => window.removeEventListener(TEST_EVENT, onFire);
  }, [pushTest]);

  /* -------------------------------- render -------------------------------- */

  const { visible, queued } = useMemo(() => selectVisible(items), [items]);
  const ordered = useMemo(() => orderItems(items), [items]);
  const visibleIds = useMemo(() => new Set(visible.map((i) => i.id)), [visible]);

  // Nothing left to expand into.
  useEffect(() => {
    if (expanded && queued.length === 0) setExpanded(false);
  }, [expanded, queued.length]);

  const userId = user?.id;

  const renderCard = (item: Item) => {
    const test = isTestId(item.id);
    switch (item.payload.t) {
      case 'alert': {
        const alert = item.payload.alert;
        return (
          <AlertCardBody
            key={item.id}
            alert={alert}
            nowMs={nowMs}
            isOwner={alert.assigned_user_id === userId}
            onOpen={() => { if (test) { dismissItem(item); return; } openAlert(alert); }}
            onDismiss={() => dismissItem(item)}
          />
        );
      }
      case 'callback': {
        const card = item.payload.card;
        return (
          <CallbackCardBody
            key={item.id}
            card={card}
            onOpen={() => { if (test) { dismissItem(item); return; } openCallback(card); }}
            onDismiss={() => dismissItem(item)}
          />
        );
      }
      case 'sms': {
        const card = item.payload.card;
        return (
          <SmsCardBody
            key={item.id}
            card={card}
            onOpen={() => { if (test) { dismissItem(item); return; } openSms(card); }}
            onDismiss={() => dismissItem(item)}
          />
        );
      }
      case 'review': {
        const card = item.payload.card;
        return (
          <ReviewReadyCardBody
            key={item.id}
            card={card}
            onOpen={() => { if (test) { dismissItem(item); return; } openReview(card); }}
            onDismiss={() => dismissItem(item)}
          />
        );
      }
      case 'outreach': {
        const card = item.payload.card;
        return (
          <OutreachAssignedCardBody
            key={item.id}
            card={card}
            onOpen={() => { if (test) { dismissItem(item); return; } openOutreach(card); }}
            onDismiss={() => dismissItem(item)}
          />
        );
      }
      case 'mention': {
        const card = item.payload.card;
        return (
          <MentionCardBody
            key={item.id}
            card={card}
            onOpen={() => { if (test) { dismissItem(item); return; } openMention(card); }}
            onDismiss={() => dismissItem(item)}
          />
        );
      }
      case 'rating':
        return (
          <RatingCardBody
            key={item.id}
            call={item.payload.call}
            isTest={test}
            onDismiss={() => dismissItem(item)}
            onInteract={() => setItems((prev) => resetBudget(prev, item.id))}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {/* Call dock — the top tier. An incoming call's Answer button outranks
          every card in the stack and every modal on the page. */}
      <div ref={dockRef} className={`${CORNER} ${Z.callDock}`}>
        <SoftphoneBar variant="stack" />
      </div>

      {/* Notification stack — rides directly on top of the dock. The container
          is click-through so its transparent padding never eats a click meant
          for the page; each card re-enables pointer events for itself. */}
      {ordered.length > 0 && (
        <div
          className={`${CORNER} ${Z.overlay} pointer-events-none flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2`}
          style={{ paddingBottom: dockH ? dockH + DOCK_GAP_PX : undefined }}
        >
          {/* Every card stays MOUNTED in one constant wrapper; a queued card is
              `hidden`, not unmounted, and expanding only swaps classes. A card
              must never lose local state to displacement or to the pill — a
              rating card mid-note that gets bumped by a hot lead comes back
              with its saved stars and half-typed note intact (seam-1 verifier
              F1). Budgets stay frozen for hidden cards via the queue model,
              which never depended on mounting. */}
          <div
            className={`flex flex-col gap-2 ${expanded ? 'pointer-events-auto max-h-[60vh] overflow-y-auto' : ''}`}
          >
            {ordered.map((item) => (
              <div key={item.id} className={expanded || visibleIds.has(item.id) ? undefined : 'hidden'}>
                {renderCard(item)}
              </div>
            ))}
          </div>
          {queued.length > 0 && (
            <OverflowPill count={queued.length} expanded={expanded} onToggle={() => setExpanded((v) => !v)} />
          )}
        </div>
      )}
    </>
  );
}
